import { getSubjectLabels } from '@/lib/settings';
import React, { useState } from 'react';
import { Clock, Home, MapPin, Users } from 'lucide-react';
import { formatTimeRangeFromStrings } from '../../lib/dateFormat';
import { formatParticipantPreview, getParticipantCount, getParticipantNames } from '../../lib/groupCourses';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  course: {
    name?: string;
    fachName?: string;
    teilnehmer?: string;
    raumName?: string;
    standortName?: string;
    standortAdresse?: string;
    startTime?: string;
    endTime?: string;
    [key: string]: unknown;
  };
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onShowDetails?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const CourseTooltip: React.FC<Props> = ({ course, children, className, style, onShowDetails, ...rest }) => {
  const [show, setShow] = useState(false);
  const participantNames = getParticipantNames(course);
  const participantCount = getParticipantCount(course);
  const { preview: participantPreview, hiddenCount } = formatParticipantPreview(participantNames, 5);

  return (
    <div className={className} style={{ ...style, zIndex: show ? 9999 : (style?.zIndex as number | undefined) }} {...rest}>
      <div
        className="relative"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        // Beim Rechtsklick weicht der Tooltip dem Kontextmenü: Mit ihm lag die
        // Karte auf Ebene 9999 und deckte die obersten Menüpunkte zu (T-0296).
        onContextMenuCapture={() => setShow(false)}
      >
        {children}
        {show && (
          <div className="absolute left-full top-0 ml-2 z-[10000] w-64 rounded-lg border bg-popover p-3 text-popover-foreground shadow-lg">
            <div className="space-y-2 text-xs">
              <div className="font-semibold text-sm border-b pb-2">{course.name}</div>
              {course.fachName && (
                <div className="flex items-start gap-2">
                  <Users className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <div className="text-muted-foreground">{getSubjectLabels().singular}:</div>
                    <div className="font-medium">{course.fachName}</div>
                  </div>
                </div>
              )}
              {(participantCount > 0 || course.teilnehmer) && (
                <div className="flex items-start gap-2">
                  <Users className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <div className="text-muted-foreground">Teilnehmer:</div>
                    {participantCount > 0 ? (
                      <div className="space-y-1">
                        <div className="font-medium">{participantCount} Teilnehmer</div>
                        <div>{participantPreview}</div>
                        {hiddenCount > 0 ? (
                          <div className="text-muted-foreground">Weitere {hiddenCount}</div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="font-medium">{course.teilnehmer}</div>
                    )}
                  </div>
                </div>
              )}
              {course.raumName && (
                <div className="flex items-start gap-2">
                  <Home className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <div className="text-muted-foreground">Raum:</div>
                    <div className="font-medium">{course.raumName}</div>
                    {course.standortName && (
                      <div className="text-muted-foreground text-xs">{course.standortName}</div>
                    )}
                    {course.standortAdresse && (
                      <div className="text-muted-foreground text-xs flex items-start gap-1">
                        <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>{course.standortAdresse}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 pt-2 border-t">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <span>
                  {formatTimeRangeFromStrings(course.startTime, course.endTime)}
                </span>
              </div>
              {participantCount > 5 ? (
                <div className="pt-1">
                  <button
                    type="button"
                    className="rounded border px-2 py-1 text-[11px] font-medium hover:bg-muted"
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowDetails?.(e);
                    }}
                  >
                    Alle anzeigen
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseTooltip;


